import React, { useState, useEffect, useRef } from "react";

import { GitHub, Link, Loader, Plus, Settings, Target } from "react-feather";
import "../../components/components.css";

import { Menu } from "react-feather";
import dayjs from "dayjs";
import { EducationItem, WorkplaceLinkConfig } from "../../client";
import { ProfileComponentProps } from "../../types";
import EditorTextButton from "../../components/atoms/EditorTextButton";
import { EditorInput } from "../../components/input/EditorInput";
import { trim } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-regular-svg-icons";
import { cn } from "../../components/utils/cn";
import {
  faArrowDown,
  faArrowUp,
  faClose,
  faRemove,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
interface WorkplaceEditorProps extends ProfileComponentProps {
  education: any;
  isEditable: boolean;
  isNew: boolean;
  onSaveItem: (education: EducationItem) => void;
  onCancelEdit?: () => void;
  onRemove: () => void;
  onShowDialog: (dialogType: string, educationId: number) => void;
  isBeingEdited: boolean;
  isLastInOrder: boolean;
  onContentImprovementStarted?: () => void;
  onContentImprovementEnded?: () => void;
  workplaceName?: string;
  workplaceLinkConfig?: WorkplaceLinkConfig;
  numberOfConnectedProjects?: number;
  removePadding?: boolean;
  pdfMode?: boolean;
  staticMode?: boolean;
  onMoveUp?: (order: number) => void;
  onMoveDown?: (order: number) => void;
  educationCount?: number;
  onProjectClickAction?: (action: string) => void;
}

function ProfileEducation({
  isDarkModeActive,
  isEditable,
  education,
  isNew,
  onSaveItem,
  isBeingEdited,
  onCancelEdit,
  onRemove,
  onShowDialog,
  isLastInOrder,
  onContentImprovementStarted,
  onContentImprovementEnded,
  workplaceLinkConfig,
  numberOfConnectedProjects,
  removePadding,
  pdfMode,
  staticMode,
  onMoveUp,
  onMoveDown,
  educationCount,
  onProjectClickAction,
}: WorkplaceEditorProps) {
  const [currentEducation, setCurrentEducation] = useState<EducationItem>(
    () => {
      return { ...education };
    },
  );
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentEducation({ ...education });
  }, [education]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the click is on a button or within the actions menu
      const isButton = target.closest("button");
      const isWithinActions = actionsRef.current?.contains(target);

      if (showActions && !isButton && !isWithinActions) {
        setShowActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  const [isMouseHovering, setIsMouseHovering] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);

  const onTitleInput = (input: string) => {
    const newEducation = { ...currentEducation, name: input };
    onSaveItem(newEducation);
    setCurrentEducation(newEducation);
  };

  const parseDate = (dateString: string) => {
    if (dateString === "" || dateString === "End Year") {
      return "";
    }

    return dayjs(dateString, "YYYY").format("YYYY");
  };

  return (
    <div className={cn("relative flex flex-row", isEditable && "group/edit")}>
      <div className="absolute left-0 flex flex-col">
        {isEditable && (
          <div className="opacity-0 group-hover/edit:opacity-100 transition-opacity duration-300 pt-1">
            <button
              onClick={() => setShowActions(!showActions)}
              className="cursor-pointer"
            >
              <FontAwesomeIcon
                icon={faEdit}
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  showActions && "rotate-180",
                )}
              />
            </button>
          </div>
        )}

        {showActions && (
          <div
            ref={actionsRef}
            className="flex flex-col gap-2 bg-white z-[9999] rounded-lg shadow-lg p-1"
            onMouseLeave={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon
                icon={faClose}
                className="w-4 h-4 text-gray-600"
              />
              <span className="text-sm text-gray-700">Close</span>
            </button>
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                if (onProjectClickAction) {
                  onProjectClickAction("remove");
                }
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-red-3" />
              <span className="text-sm text-gray-700">Delete education</span>
            </button>
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                if (onProjectClickAction) {
                  onProjectClickAction("move-up");
                }
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon
                icon={faArrowUp}
                className="w-4 h-4 text-gray-600"
              />
              <span className="text-sm text-gray-700">Move up</span>
            </button>
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                if (onProjectClickAction) {
                  onProjectClickAction("move-down");
                }
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon
                icon={faArrowDown}
                className="w-4 h-4 text-gray-600"
              />
              <span className="text-sm text-gray-700">Move down</span>
            </button>
          </div>
        )}
      </div>
      <div
        className={cn(
          "flex-grow",
          isEditable &&
            "transition-transform duration-300 group-hover/edit:translate-x-6",
        )}
        onMouseEnter={() => setIsMouseHovering(true)}
        onMouseLeave={() => setIsMouseHovering(false)}
      >
        <div className="flex flex-1 flex-row">
          {showSidebar ? (
            <div className="flex-1 flex flex-col items-center align-center">
              <EditorTextButton
                text="Remove"
                handleClick={() => onRemove()}
              ></EditorTextButton>
              {currentEducation.order > 0 ? (
                <EditorTextButton
                  text="Move up"
                  handleClick={() => {
                    if (onMoveUp) {
                      onMoveUp(currentEducation.order);
                      setShowSidebar(false);
                    }
                  }}
                ></EditorTextButton>
              ) : null}
              {educationCount && currentEducation.order < educationCount - 1 ? (
                <EditorTextButton
                  text="Move down"
                  handleClick={() => {
                    if (onMoveDown) {
                      onMoveDown(currentEducation.order);
                      setShowSidebar(false);
                    }
                  }}
                ></EditorTextButton>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-[5] flex-col">
            <div style={{ flex: 1, flexDirection: "row" }}>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {removePadding === true ? null : (
                  <div
                    className={
                      "border-[1px] border-op-dark mb-[4px] max-w-[18px] max-h-[18px] min-w-[18px] min-h-[18px] flex justify-center items-center" +
                      (isMouseHovering && isEditable
                        ? " visible"
                        : " invisible")
                    }
                    onClick={() => {
                      setShowSidebar(!showSidebar);
                    }}
                  >
                    <Settings size={12} color="#202124" />
                  </div>
                )}

                <div
                  className={
                    "flex flex-1 pr-1 " +
                    (staticMode === true
                      ? ""
                      : removePadding === true
                        ? "pl-6"
                        : "pl-4")
                  }
                >
                  <div className="grow">
                    {isEditable ? (
                      <EditorInput
                        label="Education, course or certificate name..."
                        transformValue={trim}
                        value={currentEducation.name}
                        onBlur={(
                          event: React.FocusEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          const inputValue = event.target.value;
                          setCurrentEducation((prev) => ({
                            ...prev,
                            name: inputValue,
                          }));
                          onSaveItem(currentEducation);
                        }}
                        onChange={(
                          event: React.ChangeEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          const inputValue = event.target.value;
                          setCurrentEducation((prev) => ({
                            ...prev,
                            name: inputValue,
                          }));
                        }}
                        className="text-sm"
                      />
                    ) : (
                      <span className="text-[14px] font-normal mb-[8px]">
                        {currentEducation.name}
                      </span>
                    )}
                  </div>
                  <div>
                    {isEditable ? (
                      <EditorInput
                        label="Placeholder"
                        transformValue={trim}
                        onClick={() =>
                          onProjectClickAction &&
                          onProjectClickAction("education-dates")
                        }
                        value={
                          currentEducation.startDate === ""
                            ? "when"
                            : parseDate(currentEducation.startDate) +
                              (currentEducation.startDate &&
                              currentEducation.endDate
                                ? " - "
                                : "") +
                              (currentEducation.endDate === "" ||
                              currentEducation.endDate === "End Year"
                                ? ""
                                : parseDate(currentEducation.endDate))
                        }
                        onBlur={(
                          event: React.FocusEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {}}
                        onChange={(
                          event: React.ChangeEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {}}
                        className="text-gray-6 text-sm"
                        inputClassName="text-right"
                      />
                    ) : (
                      <label className="text-details-text whitespace-nowrap">
                        {currentEducation.startDate === ""
                          ? ""
                          : parseDate(currentEducation.startDate) +
                            (currentEducation.startDate &&
                            currentEducation.endDate
                              ? " - "
                              : "") +
                            (currentEducation.endDate === "" ||
                            currentEducation.endDate === "End Year"
                              ? ""
                              : parseDate(currentEducation.endDate))}
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileEducation;
